import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { subContainerTypesApi } from './sub-container-types.api';
import { subContainerTypeKeys } from './sub-container-types.queries';
import type {
  CreateSubContainerTypeRequest,
  UpdateSubContainerTypeRequest,
  SubContainerTypesListParams
} from '../sub-container-types.types';

export function useCreateSubContainerTypeMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('containerTypes');

  return useMutation({
    mutationFn: (data: CreateSubContainerTypeRequest) =>
      subContainerTypesApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('subCreateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subContainerTypeKeys.lists()
      });
    }
  });
}

export function useUpdateSubContainerTypeMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('containerTypes');

  return useMutation({
    mutationFn: ({
      id,
      data
    }: {
      id: number;
      data: UpdateSubContainerTypeRequest;
    }) => subContainerTypesApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('subUpdateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subContainerTypeKeys.lists()
      });
    }
  });
}

export function useDeleteSubContainerTypeMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('containerTypes');

  return useMutation({
    mutationFn: (id: number) => subContainerTypesApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('subDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subContainerTypeKeys.lists()
      });
    }
  });
}

export function useBulkDeleteSubContainerTypesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('containerTypes');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<SubContainerTypesListParams> }
    ) => subContainerTypesApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('subBulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subContainerTypeKeys.lists()
      });
    }
  });
}
